import type { Security, SecurityMetadata } from '../types/Security';

export const SECURITY_IDENTIFIER_METADATA_KEYS = ['FIGI', 'CUSIP', 'ISIN', 'SEDOL'] as const;

export type SecurityIdentifierMetadataKey = (typeof SECURITY_IDENTIFIER_METADATA_KEYS)[number];

export const DEFAULT_SECURITY_IDENTIFIER_METADATA_KEYS: readonly SecurityIdentifierMetadataKey[] =
  SECURITY_IDENTIFIER_METADATA_KEYS.filter(key => key !== 'SEDOL');

function isSecurityMetadata(value: unknown): value is SecurityMetadata {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function getSecurityMetadata(metadata: Security['Metadata']): SecurityMetadata {
  if (metadata == null) {
    return {};
  }
  if (isSecurityMetadata(metadata)) {
    return metadata;
  }

  const trimmed = metadata.trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isSecurityMetadata(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

const parsedMetadataBySecurity = new WeakMap<Security, SecurityMetadata>();

export function getCachedSecurityMetadata(security: Security): SecurityMetadata {
  const cached = parsedMetadataBySecurity.get(security);
  if (cached) {
    return cached;
  }

  const metadata = getSecurityMetadata(security.Metadata);
  parsedMetadataBySecurity.set(security, metadata);
  return metadata;
}

export function getSecurityIdentifierColumnMetadataKeys(
  includeSedolColumn: boolean
): readonly SecurityIdentifierMetadataKey[] {
  return includeSedolColumn ? SECURITY_IDENTIFIER_METADATA_KEYS : DEFAULT_SECURITY_IDENTIFIER_METADATA_KEYS;
}

function trimMetadataIdentifier(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function getIdentifierValueFromSecurity(security: Security, key: SecurityIdentifierMetadataKey): string | undefined {
  return trimMetadataIdentifier(getCachedSecurityMetadata(security)[key]);
}

export function getSecurityIdentifierValues(security: Security): string[] {
  return SECURITY_IDENTIFIER_METADATA_KEYS.flatMap(key => {
    const value = getIdentifierValueFromSecurity(security, key);
    return value != null ? [value] : [];
  });
}

export function getSecurityIdentifierSearchText(security: Security | undefined): string {
  return security ? getSecurityIdentifierValues(security).join(' ') : '';
}
